import React from "react";
import { css } from "@emotion/css";
import { Product } from "@remotes/fetchProducts";

interface ProductCardProps {
  product: Product;
  getTierColor: (tier: string) => string;
  getRenewableTypeLabel: (type: string) => string;
  onUpgradeClick: (product: Product) => void;
  isLoading: boolean;
  isAppleSubscription?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  getTierColor,
  getRenewableTypeLabel,
  onUpgradeClick,
  isLoading,
  isAppleSubscription = false,
}) => (
  <div className={productCardStyle}>
    <div className={productHeaderStyle}>
      <span className={productNameStyle}>{product.display_name}</span>
      <span 
        className={tierBadgeStyle}
        style={{ backgroundColor: `${getTierColor(product.tier)}20`, color: getTierColor(product.tier) }}
      >
        {product.tier}
      </span>
    </div>
    {product.price && (
      <div className={priceStyle}>
        <div className={priceRowStyle}>
          <span className={priceTextStyle}>{product.price}</span>
          {product.discount && (
            <span className={discountBadgeStyle}>
              Save {product.discount.discountPercentage}%
            </span>
          )}
        </div>
        {product.discount ? (
          <div className={discountTextStyle}>
            Save ${product.discount.savings.toFixed(2)} vs monthly
          </div>
        ) : (
          <div className={discountTextStyle} style={{ visibility: 'hidden' }}>
            Placeholder for consistent height
          </div>
        )}
      </div>
    )}
    <div className={productCreditsStyle}>
      <div className={productCreditItemStyle}>
        <span className={productCreditIconStyle}>🎨</span>
        <span className={productCreditTextStyle}>{product.image_credits} Image Credits</span>
      </div>
      <div className={productCreditItemStyle}>
        <span className={productCreditIconStyle}>✨</span>
        <span className={productCreditTextStyle}>{product.style_credits} Style Credits</span>
      </div>
    </div>
    <div className={productTypeStyle}>
      {getRenewableTypeLabel(product.renewable_type)}
    </div>
    {isAppleSubscription ? (
      <div className={appleMessageStyle}>
        Manage in Wand iOS App
      </div>
    ) : (
      <button 
        className={upgradeButtonStyle}
        onClick={() => onUpgradeClick(product)}
        disabled={isLoading}
      >
        {isLoading ? "Loading..." : "Get Plan"}
      </button>
    )}
  </div>
);

const productCardStyle = css`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const productHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const productNameStyle = css`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
`;

const tierBadgeStyle = css`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const productCreditsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const productCreditItemStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const productCreditIconStyle = css`
  font-size: 16px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
`;

const productCreditTextStyle = css`
  font-size: 14px;
  color: #ffffff;
  font-weight: 500;
`;

const productTypeStyle = css`
  font-size: 12px;
  color: #adadad;
  font-weight: 500;
  text-transform: uppercase;
  margin-bottom: 16px;
`;

const upgradeButtonStyle = css`
  width: 100%;
  background: #0f7dff;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: #449bff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`; 

const priceStyle = css`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const priceRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const priceTextStyle = css`
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
`; 

const discountBadgeStyle = css`
  background: #0f7dff;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const discountTextStyle = css`
  font-size: 12px;
  color: #adadad;
  font-weight: 500;
`; 

const appleMessageStyle = css`
  width: 100%;
  background: rgba(255, 149, 0, 0.1);
  color: #ff9500;
  border: 1px solid rgba(255, 149, 0, 0.3);
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  cursor: default;
`;